import OutputController from "./output-controller";
import EventController from "./event-controller";
import TemplateController from "./template-controller";
import {
  IAttribute,
  IDictionary,
  IEdges,
  ILabelOptionValue,
  IOption,
  IReactConfiguration,
} from "../../typings";

class MappingController {
  private outputController: OutputController;
  private templateController: TemplateController;
  private conf: IReactConfiguration;

  constructor(
    outputController: OutputController,
    templateController: TemplateController,
    conf: IReactConfiguration,
  ) {
    this.outputController = outputController;
    this.templateController = templateController;
    this.conf = conf;

    this.mapValuesToLabelType = this.mapValuesToLabelType.bind(this);
  }

  initEventListeners(): void {
    this.addEventListenerValuesToLabelType(["label", "shape"]);
    this.addEventListenerValuesToLabelType(["label", "fold"]);
    this.addEventListenerValuesToLabelType(["label", "edge"]);
    this.addEventListenerValuesToLabelType(["label", "edges"]);
    this.addEventListenerValuesToLabelType(["label", "edges_includes"]);

    const event: string = EventController.getSaveEventName([
      "label",
      "application-method",
    ]);
    document.body
      .querySelector(this.conf.target)
      .addEventListener(event, () => {
        const sewon: string = this.outputController.getValue([
          "label",
          "application-method",
        ]);
        this.outputController
          .setValue(["label", "sewon"], sewon)
          .then(() => {})
          .catch(() => {});
      });
  }

  private addEventListenerValuesToLabelType(name: Array<string>): void {
    const option: IOption = this.templateController.getOptionByCode(name[1]);
    if (!option) {
      return;
    }
    const event: string = EventController.getSaveEventName(name);
    document.body
      .querySelector(this.conf.target)
      .addEventListener(event, this.mapValuesToLabelType);
  }

  mapValuesToLabelType(): void {
    const option: IOption =
      this.templateController.getOptionByCode("labeltype");
    if (!option) {
      return;
    }

    const shape: string = this.outputController.getValue(["label", "shape"]);
    const fold: string = this.outputController.getValue(["label", "fold"]);
    const edge: string = this.outputController.getValue(["label", "edge"]);
    const edges: IEdges = this.outputController.getValue(["label", "edges"]);
    const edgesIncluded: boolean = this.outputController.getValue([
      "label",
      "edges_includes",
    ]);

    const results: ILabelOptionValue[] = option.values.filter(
      (value: ILabelOptionValue) => {
        const attrs: IDictionary<IAttribute> = value.attributes;

        if (MappingController.isEqualToAttribute(attrs.useInMapping, false)) {
          return false;
        }

        if (!MappingController.isEqualToAttribute(attrs.shape, shape)) {
          return false;
        }

        if (!MappingController.isEqualToAttribute(attrs.fold, fold)) {
          return false;
        }

        if (
          !MappingController.isEqualToAttribute(
            attrs["edges-included"],
            edgesIncluded,
          )
        ) {
          return false;
        }

        if (!MappingController.isEqualToAttribute(attrs.edge, edge)) {
          return false;
        }

        if (
          !edges ||
          (!edges.top && !edges.right && !edges.bottom && !edges.left)
        ) {
          return (
            MappingController.isEqualToAttribute(attrs["top-fold"], false) &&
            MappingController.isEqualToAttribute(attrs["right-fold"], false) &&
            MappingController.isEqualToAttribute(attrs["bottom-fold"], false) &&
            MappingController.isEqualToAttribute(attrs["left-fold"], false)
          );
        }

        if (
          !MappingController.isEqualToAttribute(attrs["top-fold"], edges?.top)
        ) {
          return false;
        }

        if (
          !MappingController.isEqualToAttribute(
            attrs["right-fold"],
            edges?.right,
          )
        ) {
          return false;
        }

        if (
          !MappingController.isEqualToAttribute(
            attrs["bottom-fold"],
            edges?.bottom,
          )
        ) {
          return false;
        }

        return MappingController.isEqualToAttribute(
          attrs["left-fold"],
          edges?.left,
        );
      },
    );

    const labeltype: string = results[0] ? results[0].value : "flat";
    this.outputController
      .setValue(["label", "labeltype"], labeltype)
      .then(() => {})
      .catch(() => {});
  }

  private static isEqualToAttribute(attr: IAttribute, value: any): boolean {
    if (typeof attr !== "object") {
      return !value || value === "none";
    }
    if (typeof attr.value === "boolean") {
      return attr.value === !!value;
    }
    if (typeof attr.value === "number") {
      return attr.value === parseInt(value) || attr.value === parseFloat(value);
    }
    return attr.value === value;
  }

  mapLabelTypeToValues(): void {
    const sewon: string = this.outputController.getValue(["label", "sewon"]);
    if (sewon !== null) {
      this.outputController
        .setValue(["label", "application-method"], sewon)
        .then(() => {})
        .catch(() => {});
    }
    const labeltype: string = this.outputController.getValue([
      "label",
      "labeltype",
    ]);
    if (!labeltype) {
      return;
    }

    const option: IOption =
      this.templateController.getOptionByCode("labeltype");
    if (!option) {
      return;
    }
    const value: ILabelOptionValue = this.templateController.getOptionValue(
      option,
      labeltype,
    );

    if (value.attributes.shape) {
      this.outputController
        .setValue(["label", "shape"], value.attributes.shape.value)
        .then(() => {})
        .catch(() => {});
    }

    if (value.attributes.fold) {
      this.outputController
        .setValue(["label", "fold"], value.attributes.fold.value)
        .then(() => {})
        .catch(() => {});
    }

    if (value.attributes.edge) {
      this.outputController
        .setValue(["label", "edge"], value.attributes.edge.value)
        .then(() => {})
        .catch(() => {});
    }

    const newEdges: IEdges = {
      top: value.attributes["top-fold"]
        ? value.attributes["top-fold"].value
        : false,
      right: value.attributes["right-fold"]
        ? value.attributes["right-fold"].value
        : false,
      bottom: value.attributes["bottom-fold"]
        ? value.attributes["bottom-fold"].value
        : false,
      left: value.attributes["left-fold"]
        ? value.attributes["left-fold"].value
        : false,
    };

    if (this.shouldUpdateEdges(newEdges)) {
      this.outputController
        .setValue(["label", "edges"], newEdges)
        .then(() => {})
        .catch(() => {});
    }
  }

  shouldUpdateEdges(newEdges: IEdges): boolean {
    const edges: IEdges = this.outputController.getValue(["label", "edges"]);

    if (
      !newEdges.top &&
      !newEdges.right &&
      !newEdges.bottom &&
      !newEdges.left &&
      !edges
    ) {
      return false;
    }

    if (!newEdges.top !== !edges?.top) {
      return true;
    }

    if (!newEdges.right !== !edges?.right) {
      return true;
    }

    if (!newEdges.bottom !== !edges?.bottom) {
      return true;
    }

    if (!newEdges.left !== !edges?.left) {
      return true;
    }

    return false;
  }
}

export default MappingController;
